import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import './PurchaseModal.css';
import LoadingDots from './LoadingDots';
import { SportsTeamData } from '../types';

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventTitle: string;
  marketQuestion: string;
  selectedOutcome: 'yes' | 'no';
  outcomePrice: number;
  marketIcon?: string;
  eventImage?: string;
  addingLegLoading: boolean;
  parlayLegs: Array<any>;
  onAddLegToParlay: (eventTitle: string, marketQuestion: string, outcome: string, outcomePrice: number, endDate?: string, marketIcon?: string, marketId?: string, sportsTeamData?: SportsTeamData) => Promise<boolean>;
  formatNumberWithCommas: (num: number) => string;
  calculateSettlementDate: (legs: Array<any>) => string;
  marketId: string;
  endDate?: string;
  sportsTeamData?: SportsTeamData;
  propsLabels?: { yes: string; no: string };
}

const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  eventTitle,
  marketQuestion,
  selectedOutcome,
  outcomePrice,
  marketIcon,
  eventImage,
  addingLegLoading,
  parlayLegs,
  onAddLegToParlay,
  formatNumberWithCommas,
  calculateSettlementDate,
  marketId,
  endDate,
  sportsTeamData,
  propsLabels
}) => {
  const [currentOutcome, setCurrentOutcome] = useState<'yes' | 'no'>(selectedOutcome);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const isSportsMode = !!sportsTeamData && (!!sportsTeamData.team1 && !!sportsTeamData.team2 || sportsTeamData.selectedTeam === 0);
  const isDrawBet = !!sportsTeamData && sportsTeamData.selectedTeam === 0;
  // Fallback Up/Down label detection based on the event/question text
  const isUpDownText = /\bup\s*or\s*down\b/i.test(`${eventTitle} ${marketQuestion}`);
  const yesText = propsLabels?.yes || (isUpDownText ? 'Up' : 'Yes');
  const noText = propsLabels?.no || (isUpDownText ? 'Down' : 'No');

  // Reset outcome when modal opens
  useEffect(() => {
    if (isOpen) {
      // For sports mode, map selectedTeam to correct outcome
      if (sportsTeamData && sportsTeamData.selectedTeam === 0) {
        // Draw bet: keep currentOutcome as selectedOutcome but UI will hide yes/no
        setCurrentOutcome(selectedOutcome);
      } else if (sportsTeamData && sportsTeamData.team1 && sportsTeamData.team2) {
        // If team1 was selected, outcome is 'yes'; if team2 was selected, outcome is 'no'
        setCurrentOutcome(sportsTeamData.selectedTeam === 1 ? 'yes' : 'no');
      } else {
        setCurrentOutcome(selectedOutcome);
      }
    }
  }, [isOpen, selectedOutcome, sportsTeamData]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Display cents; avoid rounding extremes
  // - If price is >= 0.99 and < 1.0, show two-decimal cents via truncation (e.g., 99.95¢)
  // - If price is > 0 and < 0.01, show two-decimal cents via truncation (e.g., 0.45¢), min 0.01¢ for non-zero
  // - Otherwise keep existing integer-cent rounding behavior
  const formatPrice = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      const cents = Math.floor(p * 10000) / 100; // two-decimal cents
      const val = cents <= 0 ? 0.01 : cents;
      return val.toFixed(2);
    }
    if (p >= 0.99 && p < 1) {
      const truncatedTwoDecimals = Math.floor(p * 10000) / 100; // e.g., 0.9996 -> 99.96
      return truncatedTwoDecimals.toFixed(2);
    }
    return Math.round(p * 100).toString();
  };

  const handleAddToParlay = async () => {
    let finalMarketQuestion = marketQuestion;
    let finalPrice = outcomePrice;

    if (isSportsMode && sportsTeamData) {
      if (isDrawBet) {
        // Draw bet: use marketQuestion (which is "Draw") and outcomePrice
        finalMarketQuestion = marketQuestion;
        finalPrice = outcomePrice;
      } else {
        // Team bet: Always use the selectedTeam from sportsTeamData
        // selectedTeam: 1 = team1, 2 = team2
        const selectedTeam = sportsTeamData.selectedTeam === 1 ? sportsTeamData.team1 : sportsTeamData.team2;
        finalMarketQuestion = selectedTeam.name;
        finalPrice = selectedTeam.prob;
      }
    } else {
      // Non-sports mode: Use the appropriate price based on current selection
      finalPrice = currentOutcome === selectedOutcome ? outcomePrice : (1 - outcomePrice);
    }
    
    const success = await onAddLegToParlay(
      eventTitle,
      finalMarketQuestion,
      currentOutcome,
      finalPrice,
      endDate,
      marketIcon,
      marketId,
      sportsTeamData
    );

    if (success) {
      try {
        posthog.capture('parlay:leg_added', {
          event_title: eventTitle,
          market_question: finalMarketQuestion,
          outcome: currentOutcome,
          outcome_price: finalPrice,
          market_id: marketId,
          is_sports: !!sportsTeamData,
        });
      } catch {}
      onClose();
    } else {
      alert('Failed to add leg to parlay');
    }
  };

  if (!isOpen) return null;

  // Sports mode: determine selected team based on currentOutcome
  let selectedTeamNum: 0 | 1 | 2 = 1;
  let selectedTeamName = marketQuestion;
  let selectedTeamAbbr = '';
  let selectedTeamColor = '';

  if (isSportsMode && sportsTeamData) {
    if (isDrawBet) {
      // Draw bet
      selectedTeamNum = 0;
      selectedTeamName = 'Draw';
      selectedTeamAbbr = 'Draw';
      selectedTeamColor = '#808080'; // Gray color for draw
    } else {
      // Map currentOutcome to team selection
      // 'yes' = team1, 'no' = team2
      selectedTeamNum = currentOutcome === 'yes' ? 1 : 2;
      const selectedTeam = selectedTeamNum === 1 ? sportsTeamData.team1 : sportsTeamData.team2;
      selectedTeamName = selectedTeam.name;
      selectedTeamAbbr = selectedTeam.abbr;
      selectedTeamColor = selectedTeam.color;
    }
  }

  return (
    <div className="purchase-modal-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    }}>
      <div className="purchase-modal-content">
        <button 
          className="close-purchase-modal-btn"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="purchase-modal-header">
          {(marketIcon || eventImage) && !isSportsMode && (
            <img
              src={(marketIcon && failedImages.has(marketIcon)) ? eventImage : (marketIcon || eventImage)}
              alt={eventTitle}
              className="purchase-modal-icon"
              onError={(e) => {
                if (marketIcon && !failedImages.has(marketIcon)) {
                  const newFailedImages = new Set(failedImages);
                  newFailedImages.add(marketIcon);
                  setFailedImages(newFailedImages);
                }
              }}
            />
          )}
          <div className="purchase-modal-title">
            <div className="purchase-modal-question">{isSportsMode ? selectedTeamName : marketQuestion}</div>
            <div className="purchase-modal-selection">
              Buy{' '}
              {isSportsMode ? (
                <span className="outcome-text" style={{ color: selectedTeamColor, fontWeight: 700 }}>
                  {selectedTeamAbbr}
                </span>
              ) : (
                <span className={`outcome-text ${currentOutcome === 'yes' ? 'yes-outcome' : 'no-outcome'}`}>
                  {currentOutcome === 'yes' ? yesText : noText}
                </span>
              )}
              {' '}• {eventTitle}
            </div>
          </div>
        </div>

        {/* In draw bets, hide Yes/No or team toggle buttons */}
        {!isDrawBet && (
          <div className="outcome-price-buttons">
            {isSportsMode && sportsTeamData ? (
              <>
                <button
                  className={`outcome-price-btn ${currentOutcome === 'yes' ? 'selected' : 'faded'}`}
                  style={{
                    background: sportsTeamData.team1.color,
                    opacity: currentOutcome === 'yes' ? 1 : 0.5,
                    color: 'white'
                  }}
                  onClick={() => setCurrentOutcome('yes')}
                >
                  {sportsTeamData.team1.abbr} {formatPrice(sportsTeamData.team1.prob)}¢
                </button>
                <button
                  className={`outcome-price-btn ${currentOutcome === 'no' ? 'selected' : 'faded'}`}
                  style={{
                    background: sportsTeamData.team2.color,
                    opacity: currentOutcome === 'no' ? 1 : 0.5,
                    color: 'white'
                  }}
                  onClick={() => setCurrentOutcome('no')}
                >
                  {sportsTeamData.team2.abbr} {formatPrice(sportsTeamData.team2.prob)}¢
                </button>
              </>
            ) : (
              <>
                <button
                  className={`outcome-price-btn yes-price-btn ${currentOutcome === 'yes' ? 'selected' : ''}`}
                  onClick={() => setCurrentOutcome('yes')}
                >
                  {yesText} {formatPrice(selectedOutcome === 'yes' ? outcomePrice : (1 - outcomePrice))}¢
                </button>
                <button
                  className={`outcome-price-btn no-price-btn ${currentOutcome === 'no' ? 'selected' : ''}`}
                  onClick={() => setCurrentOutcome('no')}
                >
                  {noText} {formatPrice(selectedOutcome === 'no' ? outcomePrice : (1 - outcomePrice))}¢
                </button>
              </>
            )}
          </div>
        )}

        <button 
          className="add-to-parlay-btn"
          disabled={addingLegLoading}
          onClick={handleAddToParlay}
        >
          {addingLegLoading ? <LoadingDots className="button-loading" /> : 'Add to parlay'}
        </button>
      </div>
    </div>
  );
};

export default PurchaseModal;